import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { FeedingDashboardConfig, FeedingPen } from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import { pelletSurfaceQuality } from '../bioscopeQuality';
import { dashboardLocale } from '../dashboardLocale';
import { formatDashboardNumber } from '../dashboardNumber';
import { FeedingKpiCard } from '../FeedingKpiCard/FeedingKpiCard';
import { fallthroughTone, type KpiTone } from '../feedingChartColors';
import { formatSettingPercent } from '../settingPercent';

import { headlinePercent, type KpiCardProps, NO_BREAK, pelletValueTone } from './kpiCardFormat';
import { coverageNote } from './kpiCoverageNote';

function formatDepth(depthM: number, locale: Locale): string {
    return formatDashboardNumber(depthM, locale, { maximumFractionDigits: 1 });
}

/**
 * Each pen's own threshold gates its pellets in the query, so the site value alone
 * misstates the gate on any pen that overrides it. Compared as displayed, so two
 * thresholds that round alike do not print as a range of one value.
 */
function depthTargetText(
    pens: FeedingPen[],
    siteDepthM: number,
    t: TFunction,
    locale: Locale
): string {
    const depths = pens
        .filter((pen) => pen.fallthroughSupported)
        .map((pen) => pen.config.fallthroughDepthThresholdM);
    if (depths.length === 0) return `${formatDepth(siteDepthM, locale)}${NO_BREAK}m`;
    const min = formatDepth(Math.min(...depths), locale);
    const max = formatDepth(Math.max(...depths), locale);
    return min === max
        ? `${min}${NO_BREAK}m`
        : t('feedingModule.kpi.depthTargetRange', { min, max });
}

/**
 * The band the value falls in, in words, so the band is not colour alone (WCAG 1.4.1). The
 * settings' own wording (elevated, high) rather than the warning state's, which pellet
 * fallthrough never triggers, and the threshold as the pen table and the pellet key print it.
 */
function bandCaption(
    tone: KpiTone,
    config: FeedingDashboardConfig,
    t: TFunction,
    locale: Locale
): string | undefined {
    if (tone === 'warning') {
        return t('feedingModule.kpi.fallthroughElevated', {
            value: formatSettingPercent(config.fallthroughWarnPct, locale),
        });
    }
    if (tone === 'alert') {
        return t('feedingModule.kpi.fallthroughHigh', {
            value: formatSettingPercent(config.fallthroughAlertPct, locale),
        });
    }
    return undefined;
}

export function FallthroughKpiCard(props: KpiCardProps) {
    const { summary, config, pens } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    // Pellet counts come from the feeding camera, not from the weight estimates, so the
    // weight startup window and an estimate outage do not mask this card (IPS 15, O10;
    // 3.1.2 lists the two availabilities separately). The note says which pens it comes from.
    const pct = summary.fallthroughAvailable ? summary.fallthroughAvgPct : null;
    const { note, noteReasons } = coverageNote([{ metric: 'fallthrough' }], props, t);
    const tone = fallthroughTone(pct, config.fallthroughWarnPct, config.fallthroughAlertPct);

    return (
        <FeedingKpiCard
            label={t('feedingModule.kpi.fallthrough')}
            quality={pelletSurfaceQuality(pct != null)}
            qualitySubject="pellet"
            values={[
                {
                    ...headlinePercent(pct, 1, { locale }),
                    tone: pelletValueTone(tone),
                    caption: bandCaption(tone, config, t, locale),
                },
            ]}
            details={[
                {
                    label: t('feedingModule.kpi.depthTarget'),
                    value: depthTargetText(pens, config.fallthroughDepthThresholdM, t, locale),
                },
            ]}
            note={note}
            noteReasons={noteReasons}
        />
    );
}
